import { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';

// ponytail: React only wires a native listener for onLoad on <iframe>, not
// onError (see react-dom's per-tag nonDelegatedEvents list), so a failed
// cross-origin load can't be caught directly. This timeout is the closest
// available signal: if the frame hasn't fired onLoad within it, treat it as
// failed. The iframe stays mounted under the error, so a slow load that
// finishes late still clears it. It catches network-level failures (DNS, connection refused, a
// blocked frame) but not an HTTP error status from a server that still
// completes the response (e.g. a 403 body) — that needs a CORS-enabled
// precheck fetch of the url, which isn't verified across every document
// source yet.
const LOAD_TIMEOUT_MS = 15_000;

export interface DocumentViewerDialogProps {
  open: boolean;
  onClose: () => void;
  url: string | undefined;
  fileName: string;
  /** Optional content rendered to the left of the iframe, e.g. a document list. */
  leftPanel?: React.ReactNode;
  /** Optional extra controls rendered in the title bar, before the close button. */
  headerActions?: React.ReactNode;
}

export const DocumentViewerDialog: React.FC<DocumentViewerDialogProps> = ({
  open,
  onClose,
  url,
  fileName,
  leftPanel,
  headerActions
}) => {
  const [hasLoadError, setHasLoadError] = useState(false);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    setHasLoadError(false);
    hasLoadedRef.current = false;

    if (!open || !url) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (!hasLoadedRef.current) {
        setHasLoadError(true);
      }
    }, LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timer);
  }, [open, url]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: { width: '95vw', height: '95vh', maxWidth: 'none', m: 0 }
      }}
    >
      <DialogTitle
        sx={{
          fontSize: 16,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box
          component="span"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {fileName}
        </Box>
        <Stack direction="row" gap={1} flexShrink={0} alignItems="center">
          {headerActions}
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: '0 !important',
          gap: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        {leftPanel}
        <Box
          sx={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}
        >
          {url && (
            <Box
              component="iframe"
              src={url}
              title={fileName}
              onLoad={() => {
                hasLoadedRef.current = true;
                setHasLoadError(false);
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                height: '100%',
                alignSelf: 'stretch',
                border: 'none'
              }}
            />
          )}
          {hasLoadError && (
            <Stack
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'background.paper',
                color: 'text.secondary'
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 32 }} />
              <Typography variant="body2">
                Couldn&apos;t load this document. The link may have expired.
              </Typography>
            </Stack>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
